import { z } from 'zod';
import {
  uuidSchema,
  nameSchema,
  notesSchema,
  paginationSchema,
  lightweightFlagSchema,
} from './common.js';

export const modelSourceTypeSchema = z.enum([
  'UPLOAD',
  'MAKERWORLD',
  'PRINTABLES',
  'THINGIVERSE',
  'URL',
]);

// Create model (just name and description)
export const createModelSchema = z.object({
  name: nameSchema,
  description: z.string().max(1000).trim().optional(),
  notes: notesSchema,
});

// Update model
export const updateModelSchema = z.object({
  name: nameSchema.optional(),
  description: z.string().max(1000).trim().optional(),
  isFavorite: z.boolean().optional(),
  notes: notesSchema,
});

// Helper to convert string boolean to boolean
const stringToBooleanSchema = z.preprocess(
  (val) => {
    if (val === undefined || val === null) return undefined;
    if (val === 'true' || val === true) return true;
    if (val === 'false' || val === false) return false;
    return undefined;
  },
  z.boolean().optional()
);

// Helper to convert string number to number
const stringToNumberSchema = z.preprocess(
  (val) => {
    if (val === undefined || val === null || val === '') return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().int().min(1).max(90).optional()
);

// Query models
export const modelQuerySchema = paginationSchema
  .extend(lightweightFlagSchema.shape)
  .extend({
    search: z.string().max(100).trim().optional(),
    sourceType: modelSourceTypeSchema.optional(),
    sortBy: z.enum(['name', 'createdAt', 'updatedAt', 'addedAt']).optional().default('addedAt'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  // Collection filters
  collectionId: uuidSchema.optional(), // Single collection (includes nested)
  collectionIds: z.preprocess(
    // Ensure collectionIds is always an array (Express might send single value as string)
    (val: unknown): unknown => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(uuidSchema).max(50).optional()
  ), // Multiple collections
  uncollected: stringToBooleanSchema, // Models not in any collection
  // Smart collection filters
  isFavorite: stringToBooleanSchema, // Favorites smart collection
  recent: stringToBooleanSchema, // Combined recent filter (viewed OR added OR printed)
  recentDays: stringToNumberSchema, // Number of days for recent filters (default 7)
  recentViewed: stringToBooleanSchema, // Recently viewed models (lastViewed within recentDays)
  recentAdded: stringToBooleanSchema, // Recently added models (addedAt within recentDays)
  recentPrinted: stringToBooleanSchema, // Recently printed models (lastPrinted within recentDays)
  });

// Add file to model (file uploaded via multipart/form-data)
export const addModelFileSchema = z.object({
  notes: notesSchema,
});

// Add link to model
export const addModelLinkSchema = z.object({
  url: z.string().url().max(500),
  notes: notesSchema,
});

// Preview link metadata
export const previewLinkMetadataSchema = z.object({
  url: z.string().url().max(500),
});

// Update file
export const updateModelFileSchema = z.object({
  notes: notesSchema,
});

// Update link
export const updateModelLinkSchema = z.object({
  notes: notesSchema,
});
